// Shared helper for building Claude Code's --allowedTools string (ENG-4487).
//
// The manager spawns claude in two modes: persistent tmux session, and
// one-shot `claude -p` for scheduled tasks + webapp direct chat. Each site
// used to hand-roll its own allowedTools list, which drifted:
// the one-shot paths forgot Skill and Agent, so integration skills under
// .claude/skills/integration-... were silently invisible during scheduled-task
// execution. Agents produced apologetic "no data sources connected" outputs
// when the skills were actually on disk and their API keys were in env
// vars — they just couldn't call the Skill tool.
//
// Invariant: every Claude Code invocation the manager spawns must include
// Skill, Agent, and ToolSearch. Their absence disables integration-skill
// activation, subagent dispatch, and MCP tool binding respectively, all
// without warning. Keep that list in one place so a new spawn site
// physically cannot miss them.
//
// ENG-5926: ToolSearch added. Modern Claude Code surfaces MCP tools via
// the deferred-tool registry — tools start as schemas in ToolSearch's
// catalog and bind on first invocation. Without ToolSearch in
// `--allowedTools`, the entire MCP toolchain is invisible to a
// dispatched sub-agent. Don's empirical evidence 2026-06-03 on
// agt-aws-1: every `mcp__*` call from a Task-dispatched sub-agent
// returned "No such tool available." despite the wildcards being in
// the sub-agent's `tools:` allowlist line. ToolSearch was the
// missing piece — parent uses it for first-tool-call binding,
// sub-agents inherit nothing if they don't have it. The parent
// session also benefits (its own MCP first-call binding goes through
// the same path). Adding it on every spawn closes the entire ENG-5897
// → ENG-5905 → ENG-5922 → ENG-5924 → ENG-5926 thread.

// Order is stable for test snapshots.
const BASE_TOOLS = ['Bash', 'Read', 'Write', 'Edit', 'Grep', 'Glob', 'Agent', 'Skill', 'ToolSearch'] as const;

// Build the comma-separated allowedTools string for a Claude Code spawn.
// Each MCP server name becomes a wildcard pattern matching every tool that
// server exposes; plus the nine base built-ins (incl. ToolSearch for MCP
// lazy-load — see ENG-5926).
export function buildAllowedTools(mcpServerNames: readonly string[]): string {
  // Claude Code's allowedTools patterns use underscore-separated names; MCP
  // server IDs in .mcp.json can use hyphens (e.g. direct-chat), so normalise.
  const mcpPatterns = mcpServerNames.map((name) => `mcp__${name.replace(/-/g, '_')}__*`);
  return [...mcpPatterns, ...BASE_TOOLS].join(',');
}
